// Components
import { createApp } from 'vue';
import App from './App.vue';
import router from './router'; // Ensure this points to your router file, which might be `index.js`

// Plugins
import vuetify from './plugins/vuetify'; // Ensure you have a vuetify plugin setup
import axios from './config/axiosConfig';
import { v4 as uuidv4 } from 'uuid';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import FloatingVue from 'floating-vue';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import { createI18n } from 'vue-i18n'

library.add(fas);

const i18n = createI18n({
  locale: 'en',
  fallbackLocale: 'es',
  messages: {
    en: {
      message: {
        hello: 'Hello',
        greeting: "Hello! What can I help you with today?",
        dark: 'Dark Mode',
        light: 'Light Mode',
        expand: 'Expand',
        thumbsup: 'Helpful',
        thumbsdown: 'Not Helpful',
        copy: 'Copy',
        send: 'Send',
        notify: 'Notify me when this is resolved',
        email: 'Email Address',
        placeholder: "What was the issue with the response?",
        submit: 'Submit',
        characters_left: 'characters left',
        enter_email: 'Enter your email for resolution notice',
        how_can_i_help: "Type your question here...",
        sources: "Sources:",
        suggested_questions: "Suggested Questions",
        navigation_instructions: "Use arrow keys to navigate questions, Enter to select, or Close button to exit.",
        error: "Error",
        enter_message: "Enter your message here. Use Enter to send or Shift+Enter for a new line.",
        enter_message_with_limit: "Enter your message here. Use Enter to send or Shift+Enter for a new line. Maximum {limit} characters allowed.",
        previous_messages: "Previous Chat Messages",
        message_input: "Message Input",
        message_form: "Your Message Form",
        message_field: "Message input field",
        character_count: "Character count",
        close_suggested: "Close suggested questions panel",
        sample_questions: "Sample questions",
        show_suggested: "Show suggested questions",
        hide_suggested: "Hide suggested questions",
        close_sources: "Close sources",
        dept_sample_questions: "Here are some questions to get you started:",
        maximize: 'Maximize',
        minimize: 'Minimize',
        close: 'Close',
        changing_language: 'Changing language...',
        language: "Change language",
        feedback_placeholder: "What was the issue with the response?",
        multibot_feedback_placeholder: "What was the issue with the response? If this query returned responses from multiple chatbots, please specify which one the feedback refers to.",
        sample_question_container: "Press Enter or Space to close this panel. You can reopen it anytime by pressing the question mark button in the lower right corner."
      }
    },
    es: {
      message: {
        hello: 'Hola',
        greeting: 'Hola! ¿Cómo puedo ayudarte hoy?',
        dark: 'Modo Oscuro',
        light: 'Modo de Luz',
        expand: 'Expandir',
        thumbsup: 'Me Gusta',
        thumbsdown: 'No Me Gusta',
        copy: 'Copiar',
        notify: 'Avísame cuando esto se resuelva',
        email: 'Dirección de correo electrónico',
        placeholder: '¿Cuál fue el problema con la respuesta?',
        submit: 'Enviar',
        characters_left: 'caracteres restantes',
        enter_email: 'Introduce tu correo para notificación de resolución',
        feedback_error: 'Error de comentario',
        email_error: 'Error de correo electrónico',
        how_can_i_help: "Escribe tu pregunta aquí...",
        sources: "Fuentes:",
        suggested_questions: "Preguntas Sugeridas",
        navigation_instructions: "Usa las flechas para navegar, Enter para seleccionar, o el botón Cerrar para salir.",
        error: "Error",
        enter_message: "Ingresa tu mensaje aquí. Usa Enter para enviar o Shift+Enter para una nueva línea.",
        enter_message_with_limit: "Ingresa tu mensaje aquí. Usa Enter para enviar o Shift+Enter para una nueva línea. Máximo {limit} caracteres permitidos.",
        previous_messages: "Mensajes anteriores",
        message_input: "Campo de entrada de mensaje",
        message_form: "Formulario de mensaje",
        message_field: "Campo de mensaje",
        send: "Enviar",
        character_count: "Contador de caracteres",
        close_suggested: "Cerrar panel de preguntas sugeridas",
        sample_questions: "Preguntas de ejemplo",
        show_suggested: "Mostrar preguntas sugeridas",
        hide_suggested: "Ocultar preguntas sugeridas",
        close_sources: "Cerrar fuentes",
        dept_sample_questions: "Aquí tienes algunas preguntas para comenzar:",
        maximize: 'Maximizar',
        minimize: 'Minimizar',
        close: 'Cerrar',
        changing_language: 'Cambiando idioma...',
        language: "Cambiar idioma",
        feedback_placeholder: "¿Cuál fue el problema con la respuesta?",
        multibot_feedback_placeholder: "¿Cuál fue el problema con la respuesta? Si esta consulta devolvió respuestas de varios chatbots, por favor, especifique cuál de ellos es el que se refiere.",
        sample_question_container: "Presiona Enter o Espacio para cerrar este panel. Puedes volver a abrirlo en cualquier momento presionando el botón de preguntas en la esquina inferior derecha."

      }
    }
  }
})

const app = createApp(App);

// Registering plugins
app.use(vuetify);
app.use(router);
app.use(FloatingVue);
app.use(VueDOMPurifyHTML, {
  hooks: {
    afterSanitizeAttributes: (currentNode) => {
      // set all elements owning target to target=_blank
      if ('target' in currentNode) {
        currentNode.setAttribute('target', '_blank');
        currentNode.setAttribute('rel', 'noopener');
      }
    },
  },
});
app.use(i18n)

// Setting up global properties
app.config.globalProperties.$axios = axios;
app.config.globalProperties.$uuidv4 = uuidv4;

// Register Font Awesome component globally
app.component('font-awesome-icon', FontAwesomeIcon);

app.mount('#app');
